// Legacy offer URL redirect — decodes the JWT from /offers/<base64token>
// and redirects to the new standalone offer page.
// Resolves the correct org by matching the JWT's memberId against
// the user's Aspirex member records via their profile emails.

import {
  EmailMismatchView,
  LegacyEmailNotSetView,
  parseForbiddenError,
} from "@/components/creators/link-resolver";
import { useResolveByMemberIdQuery } from "@/lib/queries/projects";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { AlertCircle, Loader2 } from "lucide-react";
import { Button } from "@repo/ui";
import { useEffect, useMemo } from "react";

export const Route = createFileRoute("/(legacy)/offers/$token")({
  component: OfferTokenRedirect,
});

// Convert base64url (RFC 7515) to standard base64 for atob()
function base64UrlToBase64(b64url: string): string {
  const b64 = b64url.replace(/-/g, "+").replace(/_/g, "/");
  return b64 + "=".repeat((4 - (b64.length % 4)) % 4);
}

interface OfferTokenPayload {
  id: number;
  memberId: number;
  offerType?: string;
  clientId?: string;
}

// Decode a base64-encoded JWT to extract offer details
function decodeOfferToken(token: string): OfferTokenPayload | null {
  try {
    const jwt = atob(base64UrlToBase64(token));
    const payloadB64Url = jwt.split(".")[1];
    if (!payloadB64Url) return null;

    const payload = JSON.parse(atob(base64UrlToBase64(payloadB64Url)));
    if (
      typeof payload.id !== "number" ||
      typeof payload.memberId !== "number"
    ) {
      return null;
    }
    return payload as OfferTokenPayload;
  } catch {
    return null;
  }
}

function OfferTokenRedirect() {
  const { token } = Route.useParams();
  const navigate = useNavigate();

  const payload = useMemo(() => decodeOfferToken(token), [token]);

  // Reconstruct the full offer URL to use as secureUrl for email mismatch detection.
  // The secureUrl stored in the legacy DB is the full offer page URL.
  const secureUrl = useMemo(
    () => `${window.location.origin}/offers/${token}`,
    [token],
  );

  const memberId = payload?.memberId;
  const { data, isLoading, isError, error } = useResolveByMemberIdQuery(
    memberId,
    {
      enabled: !!memberId,
      secureUrl,
    },
  );

  useEffect(() => {
    if (!payload || !data) return;

    void navigate({
      to: "/o/$orgSlug/offers/$offerId",
      params: {
        orgSlug: data.orgSlug,
        offerId: String(payload.id),
      },
      search: { memberId: payload.memberId },
      replace: true,
    });
  }, [payload, data, navigate]);

  if (!payload) {
    return (
      <div className="flex min-h-svh flex-col items-center justify-center gap-4">
        <AlertCircle className="h-8 w-8 text-destructive" />
        <div className="text-center space-y-1">
          <p className="text-sm font-medium">Invalid offer link</p>
          <p className="text-sm text-muted-foreground">
            This offer link appears to be invalid or expired.
          </p>
        </div>
        <Button variant="outline" asChild>
          <Link to="/">Go to home</Link>
        </Button>
      </div>
    );
  }

  if (isError) {
    const forbiddenError = parseForbiddenError(error);
    if (forbiddenError?.type === "LEGACY_EMAIL_NOT_SET") {
      return (
        <LegacyEmailNotSetView intendedEmail={forbiddenError.intendedEmail} />
      );
    }
    if (forbiddenError?.type === "EMAIL_MISMATCH") {
      return <EmailMismatchView intendedEmail={forbiddenError.intendedEmail} />;
    }

    return (
      <div className="flex min-h-svh flex-col items-center justify-center gap-4">
        <AlertCircle className="h-8 w-8 text-destructive" />
        <div className="text-center space-y-1">
          <p className="text-sm font-medium">Something went wrong</p>
          <p className="text-sm text-muted-foreground">
            Could not resolve your offer. Please try again.
          </p>
        </div>
        <Button variant="outline" asChild>
          <Link to="/">Go to home</Link>
        </Button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex min-h-svh flex-col items-center justify-center gap-4">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        <p className="text-sm text-muted-foreground">
          Redirecting to your offer...
        </p>
      </div>
    );
  }

  return null;
}
